import toast from "react-hot-toast";
import { oidcClient } from "../redux/token";
import { ClientReportType } from "../types/ClientReportType";
import { useGenerateReportPdfMutation } from "../services/clientreports";

/**
 * Hook designed to fetch and display the pdf that is tied to the report when it gets
 * made compliant and sent to the client. This PDF copy is our proof of what we sent.
 */

type useReportPdfDataOptions = {
    generatePdf: () => Promise<ClientReportType>,
    getPdfBytes: () => Promise<string>
};

type ReportPdfResponse = {
    reportId: string,
    reportPdfId: string | null,
    pdfData: string | null
}; 

export const useReportPdfData = (masterAccountId: string | null | undefined, reportId: string | null | undefined) : useReportPdfDataOptions => {
    const [mutatePdf, mutatePdfResults] = useGenerateReportPdfMutation();

    const handleCreatePdf = () : Promise<ClientReportType> => toast.promise(new Promise<ClientReportType>((r, e) => {
        return mutatePdf({ masterAccountId, reportId }).unwrap().then(
            (response: ClientReportType) => r(response),
            (rejection: any) => e(rejection)
        );
    }), {
        loading: 'Creating the Report PDF...',
        success: `Report PDF was generated successfully.`,
        error: 'Failed to generate the Report PDF.'
    });

    const handleGetPdfBytes = () : Promise<string> => toast.promise(new Promise<string>((r, e) => {
        if (!masterAccountId || !reportId) {
            e('Report does not exist or is not available.');
        }

        let tokenData = oidcClient.getOidcToken();
        fetch(`/api/reports/master-accounts/${masterAccountId}/reports/${reportId}/pdf`, {
            method: 'GET',
            headers: {
                'Authorization': `${tokenData?.token_type} ${tokenData?.access_token}`,
                'Content-Type': 'application/json'
            }
        })
        .then((response) => response.json())
        .then((data: ReportPdfResponse) => {
            if (!data.pdfData) {
                e('Report data is invalid.');
                return;
            }
            r(data.pdfData);
        })
        .catch((error) => {
            e(error)
        })
    }), {
        loading: 'Downloading Report PDF...',
        success: `Report document was downloaded successfully.`,
        error: 'Failed to fetch the Report document.'
    });

    return {
        generatePdf: handleCreatePdf,
        getPdfBytes: handleGetPdfBytes
    }
};